import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  createExternalLab,
  updateExternalLab,
  uploadExternalLabDocument,
} from "api/externalLabs";
import {
  DOCUMENT_ACCEPT,
  EMPTY_LAB_FORM,
  LAB_LIMITS,
  documentFileError,
  toLabForm,
  toLabPayload,
  validateLabForm,
} from "utils/externalLabRules";
import { districtOptionsForState, indianStateNames } from "utils/indiaLocations";

const FIELDS = [
  { key: "labName", label: "Lab Name", required: true, max: LAB_LIMITS.nameMax },
  { key: "ownerName", label: "Lab Owner Name", required: true, max: LAB_LIMITS.ownerNameMax },
  {
    key: "contactNumber",
    label: "Contact Number",
    required: true,
    max: 20,
    hint: "10-digit number; must not be registered for another lab.",
  },
  { key: "email", label: "Email", max: LAB_LIMITS.emailMax },
  { key: "hfrId", label: "HFR ID", max: LAB_LIMITS.hfrIdMax },
  { key: "state", label: "State", kind: "state" },
  { key: "district", label: "District", kind: "district" },
  { key: "city", label: "City", max: LAB_LIMITS.cityMax },
  { key: "pincode", label: "PIN Code", max: 6, numeric: true },
  { key: "address", label: "Full Address / Street Address", max: LAB_LIMITS.addressMax, wide: true },
];

const STATE_NAMES = indianStateNames();

const ExternalLabFormDialog = ({ open, lab, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isEdit = Boolean(lab?._id);
  const fileInputRef = useRef(null);

  const [values, setValues] = useState(EMPTY_LAB_FORM);
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (!open) return;
    setValues(isEdit ? toLabForm(lab) : EMPTY_LAB_FORM);
    setFiles([]);
    setSubmitted(false);
    setServerError("");
  }, [open, lab, isEdit]);

  const errors = validateLabForm(values);

  const stateOptions = useMemo(
    () =>
      values.state && !STATE_NAMES.includes(values.state)
        ? [values.state, ...STATE_NAMES]
        : STATE_NAMES,
    [values.state],
  );
  const districtOptions = useMemo(() => districtOptionsForState(values.state), [values.state]);

  const setField = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const pickFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    const next = [...files];
    for (const file of picked) {
      const error = documentFileError(file, next.length);
      if (error) {
        setServerError(`${file.name}: ${error}`);
        return;
      }
      next.push(file);
    }
    setServerError("");
    setFiles(next);
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    setServerError("");
    if (Object.keys(errors).length) return;

    setSaving(true);
    const payload = toLabPayload(values);
    const res = isEdit
      ? await updateExternalLab(lab._id, payload)
      : await createExternalLab(payload);

    if (!res.ok) {
      setSaving(false);
      setServerError(res.error || "Something went wrong. Please try again.");
      return;
    }

    const failed = [];
    for (const file of isEdit ? [] : files) {
      const upload = await uploadExternalLabDocument(res.data._id, file);
      if (!upload.ok) failed.push(`${file.name} (${upload.error})`);
    }
    setSaving(false);

    if (isEdit) onClose(true, "Lab details updated");
    else if (failed.length)
      onClose(true, `Lab registered, but some documents were not uploaded: ${failed.join(", ")}`, "warning");
    else onClose(true, "Lab registered and pending verification");
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose(false)}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography component="span" sx={{ display: "block", fontWeight: 700, fontSize: 17 }}>
          {isEdit ? "Edit lab details" : "Add Lab"}
        </Typography>
        <Typography component="span" sx={{ display: "block", fontSize: 12.5, color: "#6B7280" }}>
          {isEdit
            ? "Correct the registration details. The verification status is not changed by an edit."
            : "A new lab starts as Pending Verification and cannot be used until it is approved."}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#F9FAFB" }}>
        {serverError && (
          <Box
            role="alert"
            sx={{
              mb: 2,
              p: 1.25,
              borderRadius: 2,
              border: "1px solid #FECACA",
              bgcolor: "#FEF2F2",
              color: "#B91C1C",
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            {serverError}
          </Box>
        )}

        <Grid container spacing={2}>
          {FIELDS.map((field) => {
            const error = submitted ? errors[field.key] : "";
            const helperText = error || field.hint || "";
            if (field.kind === "state") {
              return (
                <Grid item xs={12} sm={6} key={field.key}>
                  <Autocomplete
                    size="small"
                    options={stateOptions}
                    value={values.state || null}
                    onChange={(_, value) =>
                      // A new state invalidates the district picked under the old one.
                      setValues((prev) => ({ ...prev, state: value || "", district: "" }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        error={Boolean(error)}
                        helperText={helperText}
                        inputProps={{ ...params.inputProps, "data-testid": "lab-field-state" }}
                      />
                    )}
                  />
                </Grid>
              );
            }
            if (field.kind === "district") {
              return (
                <Grid item xs={12} sm={6} key={field.key}>
                  <Autocomplete
                    size="small"
                    freeSolo
                    disabled={!values.state}
                    options={districtOptions}
                    value={values.district || null}
                    inputValue={values.district}
                    onChange={(_, value) => setField("district", value || "")}
                    onInputChange={(_, value, reason) => {
                      if (reason !== "reset") setField("district", value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        error={Boolean(error)}
                        helperText={
                          helperText ||
                          (!values.state
                            ? "Select a State first"
                            : districtOptions.length
                              ? ""
                              : "No list for this State — type the district")
                        }
                        inputProps={{
                          ...params.inputProps,
                          maxLength: LAB_LIMITS.districtMax,
                          "data-testid": "lab-field-district",
                        }}
                      />
                    )}
                  />
                </Grid>
              );
            }
            return (
              <Grid item xs={12} sm={field.wide ? 12 : 6} key={field.key}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  required={field.required}
                  multiline={field.wide}
                  minRows={field.wide ? 2 : undefined}
                  value={values[field.key]}
                  error={Boolean(error)}
                  helperText={helperText}
                  inputProps={{
                    maxLength: field.max,
                    ...(field.numeric ? { inputMode: "numeric" } : {}),
                    "data-testid": `lab-field-${field.key}`,
                  }}
                  onChange={(event) =>
                    setField(
                      field.key,
                      field.numeric ? event.target.value.replace(/\D/g, "") : event.target.value,
                    )
                  }
                />
              </Grid>
            );
          })}
        </Grid>

        {!isEdit && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
              Supporting screenshots / documents
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "#6B7280", mb: 1 }}>
              HFR registration screenshot or other evidence. JPG, PNG or PDF, up to 5 MB each, at
              most {LAB_LIMITS.documentsMax}.
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept={DOCUMENT_ACCEPT}
              multiple
              hidden
              data-testid="lab-document-input"
              onChange={pickFiles}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AttachFileIcon />}
                disabled={files.length >= LAB_LIMITS.documentsMax}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Attach file
              </Button>
              {files.map((file, index) => (
                <Chip
                  key={`${file.name}-${index}`}
                  size="small"
                  label={file.name}
                  onDelete={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={() => onClose(false)}
          disabled={saving}
          sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          sx={{ textTransform: "none", fontWeight: 700, px: 3 }}
        >
          {saving ? "Saving…" : isEdit ? "Save changes" : "Register lab"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExternalLabFormDialog;
